import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, getCSRFCookie } from 'api/base';
import {
  storeSessionId,
  clearSessionId,
  storeHeaderCsrfToken,
  clearHeaderCsrfToken,
} from 'utils/authStorage';
import { isElectron } from 'utils/environment';
import type { User } from 'types/backend';
import type { SessionResponse } from 'utils/authStorage';

export const authKeys = {
  me: ['auth', 'me'] as const,
};

export function useMe(enabled: boolean = true) {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: () => get('me') as Promise<User | null>,
    staleTime: Infinity,
    retry: false,
    enabled,
  });
}

type LoginParams = { email: string; password: string };
export function useLogin() {
  return useMutation({
    mutationFn: async (params: LoginParams) => {
      // Ensure we have a CSRF cookie for web, though setAuthHeader handles it too.
      // For Electron, we don't need cookies but calling this doesn't hurt (it's a no-op or fetches useless cookie).
      if (!isElectron) {
        await getCSRFCookie();
      }

      const response = await post('sessionLogin', params);

      if (isElectron) {
        const sessionResponse = response as SessionResponse;
        if (sessionResponse.sessionId) {
          await storeSessionId(sessionResponse.sessionId);
        }
        if (sessionResponse.csrfToken) {
          await storeHeaderCsrfToken(sessionResponse.csrfToken);
        }
      }
      return response;
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (isElectron) {
        try {
          await post('sessionLogout');
        } catch (e) {
          console.warn('Backend logout failed or network error', e);
        }
        await clearSessionId();
        await clearHeaderCsrfToken();
      } else {
        await getCSRFCookie();
        await post('sessionLogout');
      }
    },
    onSuccess: () => {
      // clear user immediately, then refetch
      qc.setQueryData(authKeys.me, null);
      void qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

type EntraLoginParams = { idToken: string };
export function useEntraLogin() {
  return useMutation({
    mutationFn: async (params: EntraLoginParams) => {
      if (!isElectron) {
        await getCSRFCookie();
      }

      const response = await post('entraLogin', { id_token: params.idToken });

      if (isElectron) {
        const sessionResponse = response as SessionResponse;
        if (sessionResponse.sessionId) {
          await storeSessionId(sessionResponse.sessionId);
        }
        if (sessionResponse.csrfToken) {
          await storeHeaderCsrfToken(sessionResponse.csrfToken);
        }
      }
      return response;
    },
  });
}
